<template>
  <div
    ref="containerRef"
    class="w-full relative"
    :style="{ height: shouldRotateLabels ? '80px' : '40px' }"
  >
    <div class="absolute top-1/2 left-0 right-0 h-px bg-gray-800" />

    <div
      v-for="(month, index) in months"
      :key="month.toISOString()"
      class="absolute h-3 w-px bg-gray-800"
      :style="{
        left: `${index * segmentWidth}%`,
        top: 'calc(50% - 6px)'
      }"
    >
      <div
        :class="[
          'absolute capitalize font-medium whitespace-nowrap text-xs text-gray-600',
          shouldRotateLabels
            ? 'transform -rotate-45 origin-left translate-y-7'
            : 'top-4 -translate-x-1/2'
        ]"
      >
        {{ formatMonth(month) }}
      </div>
    </div>

    <TooltipProvider
      v-for="(sale, index) in sales" :key="index"
    >
      <Tooltip>
        <TooltipTrigger
          as-child
          class="absolute z-10 w-2 h-2 bg-primary rounded-full -translate-x-1/2 -translate-y-1/2 hover:w-3 hover:h-3 transition-all cursor-pointer"
        >
          <div
            :key="`${sale.datetime}-${index}`"
            :style="{
              left: `${calculateDotPosition(sale.datetime)}%`,
              top: '50%'
            }"
          />
        </TooltipTrigger>
        <TooltipContent>
          <div>
            <p class="text-base">
              {{ getSaleTooltip(sale) }}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, onMounted, onUnmounted} from 'vue';
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import {ConsumerProgramSale, ProgramSalesProps} from "@/program/components/hospital-program-sales/HospitalProgramSalesCard.vue";

const props = defineProps<ProgramSalesProps>();

const shouldRotateLabels = ref(false);
const containerRef = ref<HTMLDivElement | null>(null);

/**
 * Check if the container width is less than 500px to rotate labels.
 */
const checkWidth = () => {
    if (containerRef.value) {
        shouldRotateLabels.value = containerRef.value.offsetWidth < 500;
    }
};

/**
 * Setup resize listener on mount
 */
onMounted(() => {
    checkWidth();
    window.addEventListener('resize', checkWidth);
});

/**
 * Cleanup resize listener on unmount
 */
onUnmounted(() => {
    window.removeEventListener('resize', checkWidth);
});

/**
 * Get the min and max date of the sales.
 */
const getDateRange = () => {
    const maxDate = new Date();
    const startAtCopy = new Date(props.startAt);
    startAtCopy.setDate(1);

    maxDate.setMonth(maxDate.getMonth() + 1, 0);

    return {startAt: startAtCopy, maxDate};
};

/**
 * Get the months between the min and max date.
 */
const getMonths = () => {
    const {startAt, maxDate} = getDateRange();
    const months: Date[] = [];
    const currentDate = new Date(startAt);

    while (currentDate <= maxDate) {
        months.push(new Date(currentDate));
        currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return months;
};

/**
 * Calculate the position of a sale dot on the timeline.
 */
const calculateDotPosition = (saleDate: string) => {
    const {startAt, maxDate} = getDateRange();
    const totalDays = (maxDate.getTime() - startAt.getTime()) / (1000 * 60 * 60 * 24);
    const saleDays = (new Date(saleDate).getTime() - startAt.getTime()) / (1000 * 60 * 60 * 24);
    return (saleDays / totalDays) * 100;
};

/**
 * Format a month to a short string.
 */
const formatMonth = (date: Date, showYear: boolean = false) => {
    return date.toLocaleDateString('fr-FR', {
        month: 'short',
        year: showYear ? 'numeric' : undefined
    });
};

/**
 * Generate tooltip text for a sale
 */
const getSaleTooltip = (sale: ConsumerProgramSale) => {
    return `x${sale.quantity} le ` + new Date(sale.datetime).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const months = computed(() => getMonths());
const segmentWidth = computed(() => 100 / months.value.length);
</script>
